import type { InvoiceRecord, InvoiceRecordLineItem } from "@/repositories/invoice-records";
import { jsPDF } from "jspdf";
import { format } from "date-fns";
import { getInvoiceSubject } from "@/lib/invoices/invoice-subject";

const BILL_TO_NAME_LINES_MAX = 2;
const BILL_TO_ADDRESS_LINES_MAX = 6;

/**
 * Loads an image from a URL and returns it as a base64 data URL.
 * Returns null if loading fails.
 */
async function loadImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateInvoicePDF(invoice: InvoiceRecord, lineItems: InvoiceRecordLineItem[]): Promise<void> {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  const vendorName = invoice.vendor_data.name || "Business Name";
  const vendorPhone = invoice.vendor_data.phone || "";
  const vendorLocation = invoice.vendor_data.location || "";
  const vendorLogoUrl = invoice.vendor_data.logo || "";

  const subject = getInvoiceSubject(invoice);
  const propertyName = subject.propertyName || "N/A";
  const billToName = invoice.bill_to.name;
  const billToAddress = invoice.bill_to.address ?? "";
  const clientEmail = invoice.billing_email || "";
  const unitNumber = subject.unitNumber || "";

  // Invoice details
  const invoiceNumber = `${invoice.invoice_number}`.padStart(5, "0");
  const issueDate = invoice.issue_date ? format(new Date(invoice.issue_date), "MM/dd/yyyy") : "N/A";
  const dueDate = invoice.due_date ? format(new Date(invoice.due_date), "MM/dd/yyyy") : "N/A";
  const serviceName = subject.serviceName || "";
  const serviceCategory = subject.presentation.pdfServiceDetail || "";

  // Helper function to add new page if needed
  const checkPageBreak = (requiredHeight: number): boolean => {
    if (yPosition + requiredHeight > pageHeight - 30) {
      pdf.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // ── HEADER ────────────────────────────────────────────────────────────────
  // Logo (left side)
  let logoLoaded = false;
  if (vendorLogoUrl) {
    try {
      const logoBase64 = await loadImageAsBase64(vendorLogoUrl);
      if (logoBase64) {
        pdf.addImage(logoBase64, "PNG", margin, yPosition, 15, 15);
        logoLoaded = true;
      }
    } catch {
      // Logo loading failed, continue without it
    }
  }

  // Business name and address (next to logo)
  const textStartX = logoLoaded ? margin + 20 : margin;
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(vendorName, textStartX, yPosition + 5);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(80, 80, 80);
  if (vendorLocation) {
    pdf.text(vendorLocation, textStartX, yPosition + 10);
  }
  if (vendorPhone) {
    pdf.text(vendorPhone, textStartX, yPosition + 14);
  }

  // Invoice number and issue date (right side)
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Invoice# ${invoiceNumber}`, pageWidth - margin, yPosition + 5, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(80, 80, 80);
  pdf.text("Issue date", pageWidth - margin, yPosition + 10, { align: "right" });
  pdf.text(issueDate, pageWidth - margin, yPosition + 14, { align: "right" });

  yPosition += 20;

  // ── BLUE ACCENT BAR ───────────────────────────────────────────────────────
  pdf.setFillColor(41, 65, 122);
  pdf.rect(margin, yPosition, contentWidth, 2.5, "F");
  yPosition += 10;

  // ── BUSINESS NAME SECTION ─────────────────────────────────────────────────
  pdf.setFontSize(22);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(vendorName, margin, yPosition);
  yPosition += 8;

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(100, 100, 100);
  pdf.text("Thank you for your business.", margin, yPosition);
  yPosition += 15;

  // ── THREE-COLUMN INFO SECTION ─────────────────────────────────────────────
  // Divider lines above columns
  const colWidth = contentWidth / 3;
  const col1X = margin;
  const col2X = margin + colWidth;
  const col3X = margin + colWidth * 2;

  // Thin lines above each section
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.3);
  pdf.line(col1X, yPosition, col1X + colWidth - 5, yPosition);
  pdf.line(col2X, yPosition, col2X + colWidth - 5, yPosition);
  pdf.line(col3X, yPosition, col3X + colWidth - 5, yPosition);
  yPosition += 6;

  // Section headers
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(80, 80, 80);
  pdf.text("BILL TO", col1X, yPosition);
  pdf.text("DETAILS", col2X, yPosition);
  pdf.text("PAYMENT", col3X, yPosition);
  yPosition += 5;

  // Section content
  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(60, 60, 60);

  let billY = yPosition;
  const linesUpTo = (text: string, most: number) => (text === "" ? [] : (pdf.splitTextToSize(text, colWidth - 5) as string[]).slice(0, most));
  const billToLines = [...linesUpTo(billToName, BILL_TO_NAME_LINES_MAX), ...linesUpTo(billToAddress, BILL_TO_ADDRESS_LINES_MAX), ...linesUpTo(clientEmail, 1)];
  for (const line of billToLines) {
    pdf.text(line, col1X, billY);
    billY += 4;
  }

  let detailY = yPosition;
  pdf.text(propertyName, col2X, detailY);
  detailY += 4;
  if (unitNumber) {
    pdf.text(`Unit: ${unitNumber}`, col2X, detailY);
    detailY += 4;
  }
  if (serviceCategory) {
    pdf.text(serviceCategory, col2X, detailY);
    detailY += 4;
  }
  if (serviceName) {
    pdf.text(serviceName, col2X, detailY);
    detailY += 4;
  }

  // PAYMENT column
  let paymentY = yPosition;
  pdf.text(`Due date ${dueDate}`, col3X, paymentY);
  paymentY += 5;
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(`$${invoice.amount.toFixed(2)}`, col3X, paymentY);

  yPosition = Math.max(billY, detailY, paymentY) + 12;

  // ── LINE ITEMS TABLE ──────────────────────────────────────────────────────
  checkPageBreak(30);

  // Divider line
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.3);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 6;

  // Table header
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(80, 80, 80);

  const itemX = margin;
  const qtyX = margin + 95;
  const priceX = margin + 120;
  const amountX = pageWidth - margin;

  pdf.text("ITEM", itemX, yPosition);
  pdf.text("QTY", qtyX, yPosition);
  pdf.text("PRICE", priceX, yPosition);
  pdf.text("AMOUNT", amountX, yPosition, { align: "right" });
  yPosition += 3;

  // Divider below header
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.3);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 6;

  // Table rows
  pdf.setFontSize(9);
  for (const item of lineItems) {
    checkPageBreak(16);

    // Item name
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    const itemName = item.particular || "Item";
    pdf.text(itemName, itemX, yPosition);

    // QTY
    pdf.setFont("helvetica", "normal");
    pdf.text(item.quantity.toString(), qtyX, yPosition);

    // PRICE
    pdf.text(`$${item.unit_price.toFixed(2)}`, priceX, yPosition);

    // AMOUNT
    pdf.text(`$${item.total_price.toFixed(2)}`, amountX, yPosition, { align: "right" });

    yPosition += 5;

    // Description below item name
    if (item.description) {
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      pdf.setFont("helvetica", "normal");
      const descLines = pdf.splitTextToSize(item.description, 85);
      for (const line of descLines) {
        pdf.text(line, itemX, yPosition);
        yPosition += 4;
      }
      pdf.setFontSize(9);
    }

    yPosition += 4;

    // Row separator
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 6;
  }

  // ── TOTALS SECTION ────────────────────────────────────────────────────────
  checkPageBreak(30);
  yPosition += 4;

  const totalsLabelX = margin + 110;
  const totalsValueX = pageWidth - margin;

  // Calculate subtotal from line items
  const subtotal = lineItems.reduce((sum, item) => sum + item.total_price, 0);

  // Subtotal
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(80, 80, 80);
  pdf.text("Subtotal", totalsLabelX, yPosition);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`$${subtotal.toFixed(2)}`, totalsValueX, yPosition, { align: "right" });
  yPosition += 6;

  // Tax
  const tax = invoice.amount - subtotal;
  pdf.setTextColor(80, 80, 80);
  pdf.text("Tax", totalsLabelX, yPosition);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`$${tax >= 0 ? tax.toFixed(2) : "0.00"}`, totalsValueX, yPosition, { align: "right" });
  yPosition += 4;

  // Divider before Total Due
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.3);
  pdf.line(totalsLabelX, yPosition, pageWidth - margin, yPosition);
  yPosition += 6;

  // Total Due
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text("Total Due", totalsLabelX, yPosition);
  pdf.text(`$${invoice.amount.toFixed(2)}`, totalsValueX, yPosition, { align: "right" });

  // ── PAGE NUMBERS ──────────────────────────────────────────────────────────
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Page ${i}`, pageWidth - margin, pageHeight - 10, { align: "right" });
  }

  // Download the PDF
  const fileName = `Invoice-${invoice.invoice_number}.pdf`;
  pdf.save(fileName);
}
